// Post-auth return path + MSAL stuck-state recovery.
//
// Why this exists: authConfig hardcodes redirectUri to the bare origin and sets
// navigateToLoginRequestUrl:false, so EVERY auth redirect lands the user back on "/"
// no matter where they started. api.js has always written a "postAuthRedirect" before
// handing off to acquireTokenRedirect — nothing ever read it, which is why signing in
// from /memory dumped you on the analytics app.
const KEY = "postAuthRedirect";

// An abandoned redirect (user hit back, closed the CIAM page) would otherwise leave a
// target behind that hijacks some unrelated later boot. Auth round-trips take seconds,
// so anything older than this is stale.
const MAX_AGE_MS = 5 * 60 * 1000;
// A caller may ask for longer, up to this: the phone's sign-in goes straight to Microsoft's page
// with no button to re-save at (as /login does), and sign-up there includes an emailed code.
const LONGEST_AGE_MS = 30 * 60 * 1000;

// Never bounce back to the pages that exist only to start/finish auth — that's how you
// build a loop out of the loop fix.
const NEVER_RESTORE = ["/login", "/logout"];

export function currentPath() {
  return window.location.pathname + window.location.search + window.location.hash;
}

export function savePostAuthRedirect(path = currentPath(), { maxAgeMs } = {}) {
  try {
    if (NEVER_RESTORE.some((p) => path === p || path.startsWith(`${p}?`))) return;
    const ttl = Number.isFinite(maxAgeMs) ? { ttl: maxAgeMs } : {};
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now(), ...ttl }));
  } catch (_) {
    // Private-mode / storage-disabled: losing the return path is a downgrade, not a failure.
  }
}

// Reads and REMOVES the stored target. Returns null when there's nothing safe to restore.
// Only same-origin absolute paths are honoured (a stored value starting with "//" would be
// a protocol-relative URL pointing at another host).
export function consumePostAuthRedirect() {
  let raw = null;
  try {
    raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch (_) {
    return null;
  }
  return safePath(raw);
}

// The same target, left in place: /login reads it to know what the visitor came for (an analytics
// link gets a word about Analytics before the sign-in button) without spending the return trip.
export function peekPostAuthRedirect() {
  try {
    return safePath(sessionStorage.getItem(KEY));
  } catch (_) {
    return null;
  }
}

function safePath(raw) {
  if (!raw) return null;
  try {
    const { path, at, ttl } = JSON.parse(raw);
    if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) return null;
    const maxAge = Number.isFinite(ttl) ? Math.min(Math.max(ttl, 0), LONGEST_AGE_MS) : MAX_AGE_MS;
    if (!Number.isFinite(at) || Date.now() - at > maxAge) return null;
    if (NEVER_RESTORE.some((p) => path === p || path.startsWith(`${p}?`))) return null;
    return path;
  } catch (_) {
    return null;
  }
}

// A sign-in return that came back without an account. Safari private windows lose MSAL's
// pre-redirect state during the longer sign-up flow, and the failure was swallowed: the visitor
// landed on /login again with no word of what happened. Kept in memory, not storage — storage is
// what failed, and the note only has to reach /login on this same page load.
let authReturnFailure = null;

// Whether this load is Microsoft handing back a sign-in result: a code or an error, with the
// `state` every such response carries, in the fragment (MSAL's default response mode) or the
// query. `state` is what tells it apart from our own ?code= links — the Mac app's /device?code=.
// Read it before handleRedirectPromise, which clears the URL.
export function looksLikeAuthResponse(loc = window.location) {
  return [loc.hash, loc.search].some((s) => {
    const p = new URLSearchParams((s || "").replace(/^[#?]/, ""));
    return (p.has("code") || p.has("error")) && p.has("state");
  });
}

export function noteAuthReturnFailed(reason) {
  authReturnFailure = { reason: String(reason || "unknown") };
}

export function authReturnFailed() {
  return authReturnFailure;
}

export function clearAuthReturnFailure() {
  authReturnFailure = null;
}

// Rewrite the URL to the pre-auth path BEFORE React mounts, so BrowserRouter reads the
// restored location on its first render — no visible flash of the wrong page, no extra
// navigation. Returns the path it restored, or null.
export function restorePostAuthRedirect() {
  const target = consumePostAuthRedirect();
  if (!target || target === currentPath()) return null;
  try {
    window.history.replaceState(null, "", target);
    return target;
  } catch (_) {
    return null;
  }
}

// MSAL writes an "interaction in progress" flag before navigating to the IdP and clears it
// when the response comes back. A redirect that never completes (a second concurrent
// acquireTokenRedirect, a closed tab, a network drop mid-hop) leaves the flag set — and
// because it lives in browser storage it survives reloads, after which EVERY
// acquireTokenSilent fails with interaction_in_progress. That is the state that turned this
// app into a studio → CIAM → studio loop, and it was per-browser precisely because it was
// cached per-browser.
//
// MUST be called only AFTER handleRedirectPromise() has settled: while it is running the
// flag is load-bearing (MSAL needs it to process the auth response). Once it has settled,
// nothing on this page load is legitimately mid-interaction, so a surviving flag is stale
// by definition.
export function clearStaleInteractionState() {
  const cleared = [];
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      const keys = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && /^msal\..*interaction\.status$/.test(k)) keys.push(k);
      }
      keys.forEach((k) => {
        cleared.push(k);
        store.removeItem(k);
      });
    } catch (_) {
      // Storage unavailable — nothing to clean.
    }
  }
  if (cleared.length) console.warn("[AUTH] cleared stale MSAL interaction state:", cleared);
  return cleared;
}
